import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size" | "prefix"
> {
  size?: "xs" | "sm" | "md" | "lg";
  required?: boolean;
  /**
   * Adorno a la izquierda (un icono, normalmente). Va aquí y no posicionado por fuera: el
   * `input` trae fondo propio y pinta encima de cualquier hermano anterior.
   *
   * Se omite el `prefix` nativo de HTML, que está tipado como `string` aunque este
   * componente siempre lo renderizó como nodo — igual que `suffix`.
   */
  prefix?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      size = "md",
      prefix,
      suffix,
      required = false,
      onChange,
      ...props
    },
    ref
  ) => {
    const normalizeNumberValue = (value: string) => {
      if (
        value === "" ||
        value === "-" ||
        value === "." ||
        value === "-." ||
        value === "0." ||
        value.startsWith("-0.") ||
        /[eE]/.test(value)
      ) {
        return value;
      }

      return value.replace(/^(-?)0+(?=\d)/, "$1");
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "number") {
        const normalizedValue = normalizeNumberValue(event.currentTarget.value);
        if (normalizedValue !== event.currentTarget.value) {
          event.currentTarget.value = normalizedValue;
        }
      }

      onChange?.(event);
    };

    return (
      <div className="relative w-full">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {prefix}
          </span>
        )}
        <input
          type={type}
          className={cn(
            "flex w-full rounded-lg border-2 bg-white px-3 font-semibold shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-blue-500 focus-visible:outline-none focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            size === "xs" && "mx-0 h-6 px-0.5 py-0 text-[10px]",
            size === "sm" && "h-8 py-1 text-xs",
            size === "md" && "h-11 py-2 text-base md:text-sm",
            size === "lg" && "h-14 py-3 text-base",
            required &&
              "border-red-300 focus-visible:border-red-500 focus-visible:ring-red-500",
            className,
            prefix && "pl-8",
            suffix && "pr-8"
          )}
          ref={ref}
          onChange={handleChange}
          {...props}
        />
        {required && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500">
            *
          </span>
        )}
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {suffix}
          </span>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
